import { useEffect, useState } from "react";
import axios from "axios";
import { Modal } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import useGet from "../../../global/hooks/useGet";
import { AccountColumns } from "./Columns";
import ConfirmModal from "../../../global/components/ConfirmModal";
import useDelete from "../../../global/hooks/useDelete";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import { CustomerFilter } from "../Components/FilterComponent";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";

const initialFilterState = {
  CustomerNo: "",
  OemNo: "",
  DealerNo: "",
  CustomerName: "",
};

export const Customer = () => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [listData, setListData] = useState([]);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "CustomerNo",
    order: "desc",
  });
  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {}; //
  const initialPagination = {
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  }; //
  const [pagination, setPagination] = useState(initialPagination);
  const [filter, setFilter] = useState(initialFilterState);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filterNoValueText, setFilterNoValueText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unusedLoading, setUnusedLoading] = useState(false);
  const [showUnusedModal, setShowUnusedModal] = useState(false);
  const [unusedCustomers, setUnusedCustomers] = useState([]);
  const logData = JSON.parse(sessionStorage.getItem("userData"));

  const navigate = useNavigate();

  const breadcrumbItems = [
    {
      label: "Customer",
      href: "/customer",
    },
  ];

  // to get table data from API server
  const query = new URLSearchParams(params);
  // Remove unwanted keys
  query.delete("pageSize");
  query.delete("total");
  query.delete("current");
  const updatedQuery = query.toString();
  const url = updatedQuery
    ? `${process.env.REACT_APP_API_URL}/api/customers/?${updatedQuery}`
    : `${process.env.REACT_APP_API_URL}/api/customers/`;
  const { data, loading, error, refetch } = useGet(url);

  const deleteUrl = `${process.env.REACT_APP_API_URL}/api/customers/bulk-delete/`;
  // selectedRowKeys?.length > 1
  //   ? (deleteUrl = `${process.env.REACT_APP_API_URL}/api/customers/delete/`)
  //   : (deleteUrl = `${process.env.REACT_APP_API_URL}/api/customers/delete/${selectedRowKeys}/`);

  const {
    data: deletedData,
    loading: deletedDataLoading,
    error: deletedDataError,
    // deleteData,
    deleteMultiple,
  } = useDelete(deleteUrl);

  useEffect(() => {
    const matchedFilter = {};
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined) {
        matchedFilter[key] = modParams[key];
      }
    }

    setFilter((prev) => ({
      ...prev,
      ...matchedFilter,
    }));
  }, []); //

  const paginationConfig = {
    current: pagination.current, //current page
    pageSize: pagination.pageSize, // items per page
    total: data?.count, // total items
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
      });

      const updatedParams = {
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page: page,
      };
      setParams(updatedParams);
    },
  };

  const onSelectChange = (newSelectedRowKeys, newSelectedRow) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
  };

  const onRow = (record) => {
    return {
      onClick: (event) => {
        navigate(`/customer/info/${record?.CustomerNo}`);
      },
    };
  };

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value.replace(/['""`''""]/g, "");
    setFilter({ ...filter, [inputName]: inputValue });
  };

  const handleFilter = (e) => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        filterDict[key] = filter[key];
      }
    }
    const isEmpty = JSON.stringify(filterDict) === "{}";

    if (isEmpty) {
      setFilterNoValueText("No value exists!");
      handleReset();
    } else {
      const params = {
        limit: 10,
        sort_by: "CustomerNo",
        order: "desc",
        page: 1,
        ...filterDict,
      };
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams(params);
      refetch();
    }
  };

  //sent invitation
  const handleInvitation = (e) => {
    setIsModalOpen(true);
  };
  // reset
  const handleReset = (e) => {
    setFilter({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //
    const params = {
      limit: 10,
      sort_by: "CustomerNo",
      order: "desc",
    };
    setParams(params);
    refetch();
  };

  const onDeleteConfirm = async () => {
    try {
      // if (selectedRowKeys.length === 1) {
      //   await deleteData();
      // } else {
      await deleteMultiple({ ids: selectedRowKeys });
      // }
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
    setSelectedRowKeys([]);
  };

  const handleDeleteUnused = async () => {
    setUnusedLoading(true);
    try {
      const token = JSON.parse(sessionStorage.getItem("userData"));
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/customers/unused/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token?.access_token}`,
          },
        },
      );
      const payload = response?.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      if (!list.length) {
        openErrorNotification("No unused Customers found.");
        return;
      }
      setUnusedCustomers(list);
      setShowUnusedModal(true);
    } catch (error) {
      openErrorNotification(
        error?.response?.data?.message || "Something went wrong",
      );
    } finally {
      setUnusedLoading(false);
    }
  };

  const closeUnusedModal = () => {
    setShowUnusedModal(false);
    setUnusedCustomers([]);
  };

  const onUnusedDeleteConfirm = async () => {
    const ids = unusedCustomers
      .map((customer) => customer.CustomerNo)
      .filter((id) => id !== undefined && id !== null);
    if (!ids.length) {
      closeUnusedModal();
      return;
    }
    try {
      await deleteMultiple({ ids });
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    closeUnusedModal();
  };

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      refetch();
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong"
      );
    }
  }, [deletedData, deletedDataError]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data?.data?.map((el) => ({
        ...el,
        key: el?.CustomerNo,
      }));
      setListData(newData);
    }
  }, [data, loading]);

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems} />
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        <CustomerFilter
          filter={filter}
          handleFilterChange={handleFilterChange}
          handleFilter={handleFilter}
          handleReset={handleReset}
          setShowModal={setShowModal}
          selectedRowKeys={selectedRowKeys}
          setFilter={setFilter}
          handleInvitation={handleInvitation}
          handleDeleteUnused={handleDeleteUnused}
          unusedLoading={unusedLoading}
        />
        <ConfirmModal
          showModal={showModal}
          setShowModal={setShowModal}
          onDeleteConfirm={onDeleteConfirm}
          setSelectedRowKeys={setSelectedRowKeys}
        />
        <Modal
          open={showUnusedModal}
          onCancel={closeUnusedModal}
          closable={false}
          width={420}
          centered
          footer={null}
        >
          <div className="flex pt-4 pb-2 flex-col font-['Poppins'] w-full">
            <div className="flex items-start gap-x-4 w-full px-4">
              <span className="flex items-center justify-center w-10 h-10 bg-yellow-100 text-yellow-600 rounded-full flex-shrink-0 mt-0.5">
                <WarningOutlined className="text-2xl" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-medium leading-6 text-black m-0">
                  Are you sure you want to Delete {unusedCustomers.length} unused
                  customer
                  {unusedCustomers.length === 1 ? "" : "s"}?
                </p>
                <p className="text-[13px] leading-5 text-[#5b6b7a] mt-1.5 mb-0">
                  These Customers are not associated with any device and will be
                  permanently deleted.
                </p>
              </div>
            </div>

            <div className="mt-3 mx-4 rounded-md border border-[#e5e7eb] overflow-hidden">
              <div className="max-h-[240px] overflow-y-auto">
                <div className="grid grid-cols-[64px_1fr] gap-x-2 px-2.5 py-1.5 bg-[#f4f5f9] text-[11px] font-medium text-[#6b7280] sticky top-0 z-10">
                  <span>No</span>
                  <span>Name</span>
                </div>
                {unusedCustomers.map((customer) => (
                  <div
                    key={customer.CustomerNo}
                    className="grid grid-cols-[64px_1fr] gap-x-2 px-2.5 py-1.5 text-[13px] text-black border-t border-[#eef0f3]"
                  >
                    <span className="text-[#374151]">{customer.CustomerNo}</span>
                    <span className="truncate">
                      {customer.CustomerName || customer.Customer || "—"}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-4 w-full px-1">
              <button
                onClick={onUnusedDeleteConfirm}
                className="px-6 py-1 text-[14px] bg-[#007BFF] text-white rounded-md hover:bg-white hover:text-[#007BFF] transition shadow-none hover:shadow-[0_4px_12px_rgba(0,0,0,0.25)]"
              >
                Yes
              </button>
              <button
                onClick={closeUnusedModal}
                className="px-6 py-1 text-[14px] text-white bg-red-600 rounded-md hover:bg-white hover:text-red-600 transition shadow-none hover:shadow-[0_4px_12px_rgba(0,0,0,0.25)]"
              >
                No
              </button>
            </div>
          </div>
        </Modal>
        <GlobalTable
          columns={AccountColumns(
            hoveredRow,
            setShowModal,
            setSelectedRowKeys,
            setParams,
            params,
            logData?.role_value === 4 ||
              logData?.role_value === 5 ||
              logData?.role_value === 6 ||
              logData?.role_value === 7 ||
              logData?.role_value === 8 ||
              logData?.role_value === 9
          )}
          dataSource={listData.filter(
            (item) => item.UserId !== logData.user_id
          )}
          rowSelection={rowSelection}
          pagination={paginationConfig}
          onRow={(record, rowIndex) => {
            return {
              onMouseEnter: () => setHoveredRow(rowIndex),
              onMouseLeave: () => setHoveredRow(null),
              ...onRow?.(record, rowIndex),
            };
          }}
          loading={loading}
        />
      </div>
    </div>
  );
};
